import { ArrowLeft } from "lucide-react";
import { type GetServerSideProps } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ClientComponent } from "~/components/client-component";
import { Header } from "~/components/landing-page/header";
import { GridCtaCard } from "~/components/marketing/grid-cta-card";
import OutfitWithItemsCard from "~/components/outfit/outfit-with-items-card";
import { shuffleArray } from "~/lib/utils";
import { db } from "~/server/db";
import { api, type RouterOutputs } from "~/utils/api";

type PostType = RouterOutputs["blog"]["getBlogPostBySlug"];

type BlogPostPageProps = {
  data: PostType;
};

const Page = ({ data }: BlogPostPageProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const CTA_COPIES = [
    "Find Clothes That Match Your Style.",
    "Look, Feel, and Dress Better.",
    "Fit and Style, Personalized For You.",
    "Find Clothes for Any Situation.",
  ];

  if (!data) {
    return (
      <div className="text-center">
        <h1 className="my-8 text-3xl font-semibold tracking-tight">
          404 <br /> Blog post not found :(
        </h1>
        <Link href="/blog" className=" text-blue-500 hover:underline">
          &#8592; View all posts
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Head>
        <title>{data.title}. Glimpses AI</title>
        <meta name="description" content={data.meta_description}></meta>
      </Head>
      <Header />
      <div className="mx-auto mt-8 flex w-full flex-col items-center py-4">
        <PostHeader post={data} />
        <ClientComponent>
          <div className="w-full">
            {data.sections.map((content, contentIndex) => (
              <>
                <PostSection
                  key={`outfit-section-${contentIndex}`}
                  section={content}
                />
                {contentIndex === 1 && data && (
                  <div className="prose mx-auto my-8 px-4">
                    <GridCtaCard
                      key={`cta-${contentIndex}`}
                      copy={shuffleArray(CTA_COPIES)[0]!}
                    />
                  </div>
                )}
              </>
            ))}
          </div>
        </ClientComponent>
        <div className="prose mx-auto my-8 w-full px-4">
          <GridCtaCard
            copy={isClient ? shuffleArray(CTA_COPIES)[0]! : CTA_COPIES[0]!}
          />
        </div>

        <Footer />
      </div>
    </div>
  );
};

interface PostHeaderProps {
  post: PostType;
}

const PostHeader = ({ post }: PostHeaderProps) => {
  return (
    <div className="prose w-full px-4">
      <h1 className="w-full text-center text-3xl font-semibold tracking-tight">
        {post.title}
      </h1>
      <div className="not-prose m-auto my-4 flex w-full items-center justify-center gap-2">
        <Image
          className="h-10 w-10 rounded-full"
          src="/nihir.jpg"
          width={96}
          height={96}
          alt=""
        />
        <div className="flex flex-col items-start">
          <p className="text-gray-900">Nihir Vedd</p>
          <p className="text-gray-500">{post.updated_at.toDateString()}</p>
        </div>
      </div>
      <p className="m-auto my-4 max-w-3xl">{post.introduction}</p>
    </div>
  );
};

interface PostSectionProps {
  section: PostType["sections"][number];
}

const PostSection = ({ section }: PostSectionProps) => {
  return (
    <div className="mt-10 flex w-full flex-col items-center">
      <div className="prose w-full px-4">
        <h2 className="m-auto text-lg font-semibold">{section.header}</h2>
        <p
          className="m-auto my-2 max-w-3xl"
          dangerouslySetInnerHTML={{
            __html: cssAppliedContent(section.content),
          }}
        />
      </div>
      <LoadedOutfitSection
        outfit_ids={section.assets.flatMap((o) => o.outfit_ids)}
      />
    </div>
  );
};

const Footer = () => {
  return (
    <div className="prose mx-auto space-y-2 px-4">
      <Link href="/blog" className="flex self-start font-bold text-blue-500">
        <div>
          <ArrowLeft />
        </div>
        <div>View All Posts</div>
      </Link>
      <div className="not-prose flex w-full flex-col gap-3 rounded-lg bg-blue-50 p-4 md:flex-row md:items-center">
        <Image
          className="h-16 w-16 rounded-full"
          src="/nihir.jpg"
          width={50}
          height={50}
          alt=""
        />
        <div>
          <p className="text-lg font-semibold">About Nihir</p>
          <p>
            Nihir is a co-founder of Glimpses. He is directing his decade long
            technical experience to help men look, feel, and dress better.
          </p>
        </div>
      </div>
    </div>
  );
};

const LoadedOutfitSection = ({ outfit_ids }: { outfit_ids: string[] }) => {
  const { data } = api.outfits.getByIds.useQuery(outfit_ids, {
    enabled: !!outfit_ids.length,
  });
  if (!data) return null;
  return <OutfitWithItemsCard outfits={data} withButtons />;
};

const cssAppliedContent = (inner: string) => `
<div>
  <style>
  ul
  {
      list-style: initial;
      margin: initial;
      padding: 0 0 0 40px;
  }

  li
  {
      display: list-item;
  }
  </style>
  ${inner}
</div>
`;

export const getServerSideProps: GetServerSideProps = async ({
  res,
  params,
}) => {
  res.setHeader(
    "Cache-Control",
    "public, s-maxage=3600, stale-while-revalidate=86400",
  );

  const slug = params?.slug as string;
  const data = await db.post.findFirst({
    where: {
      slug,
    },
  });

  if (!data) {
    return { props: { data: null } };
  }

  return {
    props: {
      data: data,
    },
  };
};

export default Page;
